export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('eco-auth-token');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, { credentials: 'include', ...options, headers });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    const text = await res.text();
    try {
      const data = JSON.parse(text);
      detail = data.detail || detail;
    } catch {
      if (text && !text.startsWith('<')) detail = text.slice(0, 180);
    }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/** Authenticated file download (dashboards) — anchors cannot carry the Bearer token. */
export const downloadAuthedFile = async (path, filename) => {
  const token = localStorage.getItem('eco-auth-token');
  const res = await fetch(`${API}${path}`, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const previewAuthedFile = async (path) => {
  const token = localStorage.getItem('eco-auth-token');
  const res = await fetch(`${API}${path}`, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const url = URL.createObjectURL(await res.blob());
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) URL.revokeObjectURL(url);
  else setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export const formatEur = (value) =>
  value === null || value === undefined || value === ''
    ? '—'
    : new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(Number(value));

export const snapshotRate = (snapshot, currency) => {
  if (!snapshot) return null;
  if (snapshot.rates) return snapshot.rates[currency] ?? null;
  return snapshot[currency] ?? null;
};

export const convertedFromSnapshot = (amount, snapshot, currency) => {
  const rate = snapshotRate(snapshot, currency);
  return rate === null || rate === undefined ? null : Number(amount) * Number(rate);
};

export const statusBadge = (status) => {
  if (['confirmed', 'available', 'published', 'paid', 'accepted', 'sent'].includes(status)) return 'bg-eco-green/15 text-eco-green';
  if (['cancelled', 'rejected', 'failed', 'declined'].includes(status)) return 'bg-red-100 text-red-600';
  if (['payment_pending', 'awaiting_customer', 'alternative_proposed', 'pending', 'review', 'on_hold'].includes(status)) return 'bg-amber-100 text-amber-800';
  return 'bg-blue-100 text-blue-800';
};

export const canonicalBadge = (status) => {
  if (['CONFIRMED', 'IN_PROGRESS', 'COMPLETED'].includes(status)) return 'bg-eco-green/15 text-eco-green';
  if (['CANCELLED'].includes(status)) return 'bg-red-100 text-red-600';
  if (['AWAITING_PAYMENT', 'QUOTED', 'REFUND_PENDING'].includes(status)) return 'bg-amber-100 text-amber-800';
  if (['REFUNDED'].includes(status)) return 'bg-blue-100 text-blue-800';
  return 'bg-eco-cream text-eco-ink/75 border border-eco-ink/15';
};

export const requestStatusLabel = (status) => ({
  draft: 'Draft',
  request_sent: 'Request Sent',
  waiting_partner: 'Waiting for Partner',
  available: 'Partner Confirmed',
  alternative_proposed: 'Alternative Proposed',
  awaiting_customer: 'Proposal Sent',
  payment_pending: 'Payment Pending',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  pending: 'Request Sent',
  partner_confirmed: 'Partner Confirmed',
  voucher_issued: 'Confirmed',
}[status] || status);

export const inputCls = 'eco-input';
export const selectCls = 'eco-select min-h-10 min-w-[8.5rem] rounded-[10px] border border-eco-ink/15 bg-white px-3 pr-8 text-sm font-medium text-eco-ink outline-none transition-colors focus:border-eco-green focus:ring-2 focus:ring-eco-green/10';

/**
 * Public Rent-a-Car specifications: a spec is rendered ONLY when it holds a real,
 * confirmed public value. Empty/null/placeholder fields are hidden entirely —
 * the "Por verificar" verification status is Admin-only and must never leak here.
 * When Admin later enters a real value, the spec appears automatically.
 */
export const confirmedVehicleSpecs = (vehicle, t) => {
  const v = vehicle || {};
  const specs = [];
  if (v.air_conditioning === true || v.air_conditioning === false) {
    specs.push({ key: 'ac', label: t('rent.ac'), value: v.air_conditioning ? t('rent.yes') : t('rent.no') });
  }
  if (Array.isArray(v.pickup_dropoff) && v.pickup_dropoff.length > 0) {
    specs.push({ key: 'pickup', label: t('rent.pickup'), value: v.pickup_dropoff.join(' / ') });
  }
  if (typeof v.insurance_public === 'string' && v.insurance_public.trim()) {
    specs.push({ key: 'insurance', label: t('rent.insurance'), value: v.insurance_public });
  }
  if (typeof v.reservation_terms_public === 'string' && v.reservation_terms_public.trim()) {
    specs.push({ key: 'reservation', label: t('rent.reservation'), value: v.reservation_terms_public });
  }
  return specs;
};
